/**
 * Geometry for the room-mode stem plot (room-mode-calculator's "Mode
 * spectrum"): a log-frequency x position per room mode, plus the Schroeder
 * crossover's own x position. Deliberately no y scale — stem height there is
 * a fixed weight per mode TYPE (axial/tangential/oblique), not a data value,
 * so there is nothing for a y axis to read (see `crosshair.ts`'s note on
 * `PlotDescriptor.y` being absent for exactly this reason).
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLogFreqScale, logFreqToX, type LogFreqScale } from './scales';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks } from './geometry';

export interface ModeStemInput {
  freqHz: number;
}

export interface ModeStemGeometryInput {
  modes: ModeStemInput[];
  schroederHz: number;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface PositionedModeStem {
  index: number;
  x: number;
}

export interface ModeStemGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  xTicks: AxisTicks;
  /** Plot-local px of the Schroeder line, clamped into [fMin, fMax]. */
  schroederX: number;
  /** One entry per mode in range [fMin, fMax], `index` into the ORIGINAL
   *  `modes` array (so a caller can look up type/label/etc back on its own
   *  richer mode objects). Modes outside the range are omitted, matching the
   *  bespoke chart's own behaviour of not drawing them at all. */
  stems: PositionedModeStem[];
}

export function computeModeStemGeometry(opts: ModeStemGeometryInput): ModeStemGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 250;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);
  const schroederX = logFreqToX(xScale, Math.min(Math.max(opts.schroederHz, fMin), fMax));

  if (frame.plotW <= 0) {
    return { empty: true, frame, fMin, fMax, xScale, xTicks, schroederX, stems: [] };
  }

  const stems: PositionedModeStem[] = [];
  opts.modes.forEach((m, index) => {
    if (m.freqHz < fMin || m.freqHz > fMax) return;
    stems.push({ index, x: logFreqToX(xScale, m.freqHz) });
  });

  return { empty: false, frame, fMin, fMax, xScale, xTicks, schroederX, stems };
}
